import React, { useState, useEffect } from 'react';
import './AuditLogViewer.css';
import { 
  getAuditLogs, 
  getFilteredLogs, 
  clearAuditLogs, 
  exportLogsToExcel,
  getActionColor,
  getActionIcon
} from './auditLog';
import * as XLSX from 'xlsx';

const AuditLogViewer = ({ currentUser }) => {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    searchTerm: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    const allLogs = getAuditLogs();
    setLogs(allLogs);
  };

  const applyFilters = () => {
    const filtered = getFilteredLogs(filters);
    setLogs(filtered);
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entityType: '',
      searchTerm: '',
      startDate: '',
      endDate: ''
    });
    loadLogs();
  };

  const handleClearAllLogs = () => {
    if (window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
      clearAuditLogs();
      loadLogs();
      alert('All audit logs have been cleared.');
    }
  };

  const handleExportToExcel = () => {
    const exportData = exportLogsToExcel(logs);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
    XLSX.writeFile(wb, `Audit_Logs_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      'CALL_LOG': 'Call Log',
      'ASSET': 'Asset',
      'TRAINING': 'Training',
      'CONTRACT': 'Contract',
      'PM_CAL': 'PM/Calibration',
      'REPORT': 'Report',
      'USER': 'User Management'
    };
    return labels[type] || type;
  };

  // Check if user is admin or manager
  const canViewLogs = currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager');

  if (!currentUser) {
    return (
      <div className="audit-log-viewer">
        <div className="access-denied">
          <h2>⏳ Loading...</h2>
          <p>Please wait while we verify your access...</p>
        </div>
      </div>
    );
  }

  if (!canViewLogs) {
    return (
      <div className="audit-log-viewer">
        <div className="access-denied">
          <h2>⛔ Access Denied</h2>
          <p>You do not have permission to view audit logs. Only administrators and managers can access this feature.</p>
          <p>Your role: <strong>{currentUser.role || 'Unknown'}</strong></p>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-log-viewer">
      <div className="viewer-header">
        <h1>📋 Audit Log Viewer</h1>
        <p className="subtitle">Track all data changes, deletions, and uploads</p>
      </div>

      <div className="viewer-controls">
        <div className="control-buttons">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            🔍 {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleExportToExcel}
            disabled={logs.length === 0}
          >
            📥 Export to Excel
          </button>
          {currentUser.role === 'admin' && (
            <button 
              className="btn btn-danger"
              onClick={handleClearAllLogs}
              disabled={logs.length === 0}
            >
              🗑️ Clear All Logs
            </button>
          )}
        </div>
        <div className="log-count">
          Total Logs: <strong>{logs.length}</strong>
        </div>
      </div>

      {showFilters && (
        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label>Action:</label>
              <select 
                value={filters.action}
                onChange={(e) => setFilters({...filters, action: e.target.value})}
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="UPLOAD">Upload</option>
                <option value="DOWNLOAD">Download</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Entity Type:</label>
              <select 
                value={filters.entityType}
                onChange={(e) => setFilters({...filters, entityType: e.target.value})}
              >
                <option value="">All Types</option>
                <option value="CALL_LOG">Call Log</option>
                <option value="ASSET">Asset</option>
                <option value="TRAINING">Training</option>
                <option value="CONTRACT">Contract</option>
                <option value="PM_CAL">PM/Calibration</option>
                <option value="REPORT">Report</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Search:</label>
              <input 
                type="text"
                placeholder="Search details, ID, user..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>Start Date:</label>
              <input 
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>
            <div className="filter-group">
              <label>End Date:</label>
              <input 
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>
            <div className="filter-group filter-actions">
              <label>&nbsp;</label>
              <div className="filter-buttons">
                <button className="btn btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
                <button className="btn btn-secondary" onClick={clearFilters}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="logs-container">
        {logs.length === 0 ? (
          <div className="no-logs">
            <p>📭 No audit logs found</p>
            {filters.action || filters.entityType || filters.searchTerm ? (
              <p>Try adjusting your filters</p>
            ) : (
              <p>Logs will appear here when users create, update, delete, or upload data</p>
            )}
          </div>
        ) : (
          <div className="logs-table-wrapper">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Entity ID</th>
                  <th>Details</th>
                  <th>User</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className={`log-row action-${log.action.toLowerCase()}`}>
                    <td className="timestamp">{formatDate(log.timestamp)}</td>
                    <td>
                      <span 
                        className="action-badge"
                        style={{ backgroundColor: getActionColor(log.action) }}
                      >
                        {getActionIcon(log.action)} {log.action}
                      </span>
                    </td>
                    <td>{getEntityTypeLabel(log.entityType)}</td>
                    <td className="entity-id">{log.entityId || '-'}</td>
                    <td className="details-cell" title={log.details}>
                      {log.details.length > 50 
                        ? log.details.substring(0, 50) + '...' 
                        : log.details}
                    </td>
                    <td className="user-cell">
                      {log.user ? (
                        <div className="user-info">
                          <span className="user-name">{log.user.name}</span>
                          <span className="user-username">@{log.user.username}</span>
                        </div>
                      ) : (
                        <span className="unknown-user">Unknown</span>
                      )}
                    </td>
                    <td>
                      {log.user && (
                        <span className={`role-badge role-${log.user.role}`}>
                          {log.user.role}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="legend">
        <h4>Legend:</h4>
        <div className="legend-items">
          <span className="legend-item"><span className="dot" style={{backgroundColor: '#22c55e'}}></span> Create</span>
          <span className="legend-item"><span className="dot" style={{backgroundColor: '#3b82f6'}}></span> Update</span>
          <span className="legend-item"><span className="dot" style={{backgroundColor: '#dc2626'}}></span> Delete</span>
          <span className="legend-item"><span className="dot" style={{backgroundColor: '#f59e0b'}}></span> Upload</span>
          <span className="legend-item"><span className="dot" style={{backgroundColor: '#8b5cf6'}}></span> Download</span>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
